import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FaCircle,
  FaCalendarAlt,
  FaPaperclip,
} from 'react-icons/fa';
import { Avatar } from '../Avatar';
import { getRiskColor } from '../../styles/designSystem';

// Status columns configuration (same as ProjectPlan KanbanBoard)
const STATUS_COLUMNS = [
  { id: 'NOT_STARTED', title: 'Not Started', color: '#888', bgColor: 'bg-[#fafafa]', borderColor: 'border-[#eaeaea]' },
  { id: 'IN_PROGRESS', title: 'In Progress', color: '#3B82F6', bgColor: 'bg-[#f5f5f5]', borderColor: 'border-[#eaeaea]' },
  { id: 'ON_HOLD', title: 'On Hold', color: '#F59E0B', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  { id: 'COMPLETED', title: 'Completed', color: '#10B981', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
];

const getPriorityColor = (priority?: string | null): string => {
  switch (priority) {
    case 'HIGH': return '#EF4444';
    case 'MEDIUM': return '#F59E0B';
    case 'LOW': return '#10B981';
    default: return '#aaa';
  }
};

const getPriorityLabel = (priority?: string | null): string => {
  switch (priority) {
    case 'HIGH': return 'High';
    case 'MEDIUM': return 'Medium';
    case 'LOW': return 'Low';
    default: return 'None';
  }
};

interface ActionItemKanbanCardProps {
  item: ProjectActionItem;
  projectData: ProjectInterface;
  isDragging?: boolean;
}

const ActionItemKanbanCard: React.FC<ActionItemKanbanCardProps> = ({ item, projectData, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const owner = projectData?.users?.find(u => u.id === item.owner_id);
  const assignee = projectData?.users?.find(u => u.id === item.assigned_to);
  const dueDate = item.due_date ? new Date(item.due_date) : null;
  const isOverdue = dueDate && dueDate < new Date() && item.status !== 'COMPLETED';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        group bg-white rounded-xl border border-[#f5f5f5] p-4 mb-3
        shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-all duration-200 cursor-grab active:cursor-grabbing
        ${isDragging ? 'opacity-50 shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-2 ring-[#171717]' : ''}
      `}
    >
      {/* Risk & Priority Indicator Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FaCircle
            className="w-2 h-2"
            style={{ color: getRiskColor(item.risks || '') }}
          />
          <span className="text-[10px] uppercase tracking-wide text-[#aaa]">
            {item.risks || 'None'}
          </span>
        </div>
        {item.priority && (
          <span
            className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded"
            style={{ color: getPriorityColor(item.priority), backgroundColor: `${getPriorityColor(item.priority)}15` }}
          >
            {getPriorityLabel(item.priority)}
          </span>
        )}
      </div>

      {/* Item Name */}
      <h4 className="text-sm font-medium text-[#171717] mb-2 line-clamp-2 leading-snug">
        {item.name || 'Untitled Action Item'}
      </h4>

      {/* Description Preview */}
      {item.description && (
        <p className="text-xs text-[#888] mb-3 line-clamp-2 leading-relaxed">
          {item.description}
        </p>
      )}

      {/* Footer: Due Date, Docs, Avatars */}
      <div className="flex items-center justify-between pt-2 border-t border-[#f5f5f5]">
        <div className="flex items-center gap-3">
          {/* Due Date */}
          {dueDate && (
            <div className={`flex items-center gap-1 text-[11px] ${isOverdue ? 'text-red-500' : 'text-[#aaa]'}`}>
              <FaCalendarAlt className="w-3 h-3" />
              <span>
                {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          )}

          {/* Document Indicator */}
          {item.document && (
            <div className="flex items-center gap-1 text-[11px] text-[#aaa]">
              <FaPaperclip className="w-3 h-3" />
            </div>
          )}
        </div>

        {/* Avatars */}
        <div className="flex items-center -space-x-1">
          {owner && (
            <Avatar
              firstName={owner.first_name}
              lastName={owner.last_name}
              email={owner.email}
              size={22}
              className="ring-2 ring-white"
            />
          )}
          {assignee && assignee.id !== owner?.id && (
            <Avatar
              firstName={assignee.first_name}
              lastName={assignee.last_name}
              email={assignee.email}
              size={22}
              className="ring-2 ring-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Droppable Column
interface DroppableColumnProps {
  column: typeof STATUS_COLUMNS[0];
  items: ProjectActionItem[];
  projectData: ProjectInterface;
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({ column, items, projectData }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const itemIds = useMemo(() => items.map(i => i.id), [items]);

  return (
    <div className={`flex flex-col min-w-[312px] w-[312px] ${column.bgColor} rounded-2xl border ${column.borderColor} ${isOver ? 'ring-2 ring-[#171717]' : ''}`}>
      {/* Column Header */}
      <div className="p-4 border-b border-[#f5f5f5]/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: column.color }}
            />
            <h3 className="text-sm font-semibold text-[#444]">
              {column.title}
            </h3>
          </div>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: `${column.color}20`,
              color: column.color
            }}
          >
            {items.length}
          </span>
        </div>
      </div>

      {/* Cards Container */}
      <div
        ref={setNodeRef}
        className="flex-1 p-3 overflow-y-auto max-h-[calc(100vh-300px)] min-h-[200px] custom-scrollbar"
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {items.map(item => (
            <ActionItemKanbanCard
              key={item.id}
              item={item}
              projectData={projectData}
            />
          ))}
        </SortableContext>

        {/* Empty State */}
        {items.length === 0 && (
          <div className={`flex flex-col items-center justify-center py-8 text-[#aaa] border-2 border-dashed rounded-xl ${isOver ? 'border-indigo-300 bg-[#f5f5f5]/50' : 'border-[#eaeaea]'}`}>
            <div className="w-10 h-10 rounded-full bg-[#f5f5f5] flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-[#ccc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xs">No items</p>
            <p className="text-[10px] text-[#ccc] mt-1">Drag items here</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Card being dragged overlay
const DragOverlayCard: React.FC<{ item: ProjectActionItem; projectData: ProjectInterface }> = ({ item, projectData }) => {
  return (
    <div className="bg-white rounded-xl border border-[#eaeaea] p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-2 ring-[#171717] w-[312px] rotate-3">
      <ActionItemKanbanCard item={item} projectData={projectData} isDragging />
    </div>
  );
};

// Main Kanban Board Component
interface ActionItemsKanbanBoardProps {
  projectId: string;
  projectData: ProjectInterface;
  filteredItems: ProjectActionItem[];
  onUpdateItem: (item: ProjectActionItem) => Promise<void>;
  onProjectUpdate?: () => Promise<void>;
}

export const ActionItemsKanbanBoard: React.FC<ActionItemsKanbanBoardProps> = ({
  projectData,
  filteredItems,
  onUpdateItem,
  onProjectUpdate,
}) => {
  const [activeItem, setActiveItem] = useState<ProjectActionItem | null>(null);
  const [localItems, setLocalItems] = useState<ProjectActionItem[]>(filteredItems);

  // Sync local items with filtered items when they change
  React.useEffect(() => {
    setLocalItems(filteredItems);
  }, [filteredItems]);

  // Group items by status
  const itemsByStatus = useMemo(() => {
    const grouped: Record<string, ProjectActionItem[]> = {};
    STATUS_COLUMNS.forEach(col => {
      grouped[col.id] = localItems.filter(item => item.status === col.id);
    });
    return grouped;
  }, [localItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const item = filteredItems.find(i => i.id === active.id);
    if (item) {
      setActiveItem(item);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over) return;

    const activeItemId = active.id as string;
    const overId = over.id as string;

    // Find the target column
    let targetColumnId: string | null = null;

    if (STATUS_COLUMNS.some(col => col.id === overId)) {
      targetColumnId = overId;
    } else {
      for (const col of STATUS_COLUMNS) {
        if (itemsByStatus[col.id]?.some(i => i.id === overId)) {
          targetColumnId = col.id;
          break;
        }
      }
    }

    if (!targetColumnId) return;

    const item = filteredItems.find(i => i.id === activeItemId);
    if (!item || item.status === targetColumnId) return;

    // Optimistic update
    const updatedItem = { ...item, status: targetColumnId };
    setLocalItems(prev => prev.map(i => i.id === item.id ? updatedItem : i));

    try {
      await onUpdateItem(updatedItem);
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Failed to update action item status:', error);
      // Revert on error
      setLocalItems(prev => prev.map(i => i.id === item.id ? item : i));
    }
  };

  return (
    <div className="kanban-board overflow-x-auto">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .kanban-board::-webkit-scrollbar {
          height: 8px;
        }
        .kanban-board::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .kanban-board::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .kanban-board::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 pb-4">
          {STATUS_COLUMNS.map(column => (
            <DroppableColumn
              key={column.id}
              column={column}
              items={itemsByStatus[column.id] || []}
              projectData={projectData}
            />
          ))}
        </div>

        <DragOverlay>
          {activeItem && (
            <DragOverlayCard
              item={activeItem}
              projectData={projectData}
            />
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default ActionItemsKanbanBoard;
